import { buildSessionArc, CROSSFADE_SECONDS, SESSION_TARGETS } from "./sessionArc.js";
import { getCandidateNaturals, NATURALS_AFFINITY } from "../library/naturalsIndex.js";
import { pickBreathing } from "../library/breathingIndex.js";
import {
  learnedContribution,
  seededUnit,
  weightedPick,
} from "./learningScore.js";
import { generateNarrative } from "./narrative.js";
import {
  getRecentAmbianceIds,
  getRecentTrackIds,
  loadPreferences,
  loadPreferencesSync,
  saveSessionStart,
} from "../memory/aiMemory.js";
import { isAuriaIntent } from "../domain/intents.js";

/* Les favoris accessibles sont sélectionnés avant les autres ambiances. */
const FAVORITE_BONUS = 40;
const NATURAL_LEARNING_FACTOR = 14;
const MIN_SECONDARY_GAP = 0.16;
const SECONDARY_CHANCE_BY_DURATION = Object.freeze({ "30sec": 0.2, "2min": 0.5, "5min": 0.7, "10min": 1 });

export const VALID_AURIA_DURATIONS = new Set([
  "30sec",
  "2min",
  "5min",
  "10min",
]);

function rotatingSeed(uid = "anon", regenerationCount = 0) {
  const key = `auria:v1:seed:${uid}`;
  let rotation = 0;

  try {
    rotation = Number.parseInt(localStorage.getItem(key) || "0", 10) || 0;

    if (regenerationCount === 0) {
      rotation = (rotation + 1) % 1000000;
      localStorage.setItem(key, String(rotation));
    }
  } catch {}

  let hash = rotation * 2654435761;

  for (let index = 0; index < uid.length; index += 1) {
    hash = (hash << 5) - hash + uid.charCodeAt(index);
  }

  return Math.abs((hash | 0) + regenerationCount * 7919);
}

function recentPenalty(id, recentIds) {
  const index = recentIds.indexOf(id);
  if (index === 0) return 24;
  if (index === 1) return 14;
  if (index >= 2) return 6;
  return 0;
}

function clampVolume(value) {
  return Math.max(0.18, Math.min(0.9, Number(value) || 0));
}

function learnedVolume(preferences, intent, id, fallback) {
  const stat = preferences?.learning?.volumes?.[intent]?.[id];
  const mean = Number(stat?.mean);

  if (!Number.isFinite(mean) || Number(stat?.samples) < 2) return fallback;
  return clampVolume(mean);
}

function pairsTogether(left, right) {
  const a = Array.isArray(left?.pairsWell) ? left.pairsWell : [];
  const b = Array.isArray(right?.pairsWell) ? right.pairsWell : [];
  return a.includes(right.id) || b.includes(left.id);
}

function enforceVolumeContrast(primary, secondary) {
  const ceiling = clampVolume(primary.volume - MIN_SECONDARY_GAP);
  return secondary.volume <= ceiling ? secondary : { ...secondary, volume: ceiling };
}

function scoreNatural({
  sound,
  intent,
  seed,
  favoriteNatureSoundIds,
  recentAmbianceIds,
  preferences,
}) {
  let score = sound.strength * 40;
  const favorite = favoriteNatureSoundIds.includes(sound.id);

  if (favorite) score += FAVORITE_BONUS;

  const stat = preferences?.learning?.naturals?.[intent]?.[sound.id];
  score += learnedContribution(stat, NATURAL_LEARNING_FACTOR);
  score -= recentPenalty(sound.id, recentAmbianceIds);
  score += seededUnit(seed, sound.id) * 8;

  return {
    ...sound,
    favorite,
    score,
    stat,
  };
}

function toAmbiance(scored, role, preferences, intent) {
  const base = role === "primary" ? 0.58 : 0.34;
  const strengthBonus = (Math.max(1, Number(scored.strength) || 1) - 2) * 0.06;
  const favoriteBonus = scored.favorite ? 0.05 : 0;
  const fallback = clampVolume(base + strengthBonus + favoriteBonus);

  return {
    id: scored.id,
    name: scored.name,
    file: scored.file,
    image: scored.image,
    role,
    strength: scored.strength,
    favorite: scored.favorite,
    volume: learnedVolume(preferences, intent, scored.id, fallback),
  };
}

function pickAmbiances({
  intent,
  durationKey,
  naturalSounds,
  isPremium,
  seed,
  recentAmbianceIds,
  favoriteNatureSoundIds,
  preferences,
}) {
  const candidates = getCandidateNaturals(intent, naturalSounds, isPremium);
  const candidateIds = new Set(candidates.map((sound) => sound.id));
  for (const sound of naturalSounds) {
    if ((!isPremium && sound.premium) || !sound.file || !favoriteNatureSoundIds.includes(sound.id) || candidateIds.has(sound.id)) continue;
    candidates.push({ ...sound, strength: 1, pairsWell: NATURALS_AFFINITY[sound.id]?.pairsWell || [] });
    candidateIds.add(sound.id);
  }
  const scored = candidates.filter((sound) => Boolean(sound.file))
    .map((sound) =>
      scoreNatural({
        sound,
        intent,
        seed,
        favoriteNatureSoundIds,
        recentAmbianceIds,
        preferences,
      }),
    )
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return [];

  const favorites = scored.filter((sound) => sound.favorite);
  const primary = weightedPick(favorites.length ? favorites : scored, seed, 5);
  if (!primary) return [];

  const result = [toAmbiance(primary, "primary", preferences, intent)];

  /* La seconde ambiance doit s'accorder avec la première : hors paire connue,
     Auria préfère une seule ambiance plutôt qu'un mélange incohérent. */
  const compatible = scored
    .filter((sound) => sound.id !== primary.id && pairsTogether(primary, sound))
    .map((sound) => ({ ...sound, score: sound.score + sound.strength * 6 + (sound.favorite ? 12 : 0) }))
    .sort((a, b) => b.score - a.score);

  if (!compatible.length) return result;

  /* Plus la séance est courte, moins une seconde couche a le temps d'être
     perçue : la superposition devient rare sur les formats brefs. */
  if (seededUnit(seed, "secondary-ambiance") > (SECONDARY_CHANCE_BY_DURATION[durationKey] ?? 0.5)) {
    return result;
  }

  const favoritePairs = compatible.filter((sound) => sound.favorite);
  const discoveryPairs = compatible.filter((sound) => !sound.favorite);
  let secondaryPool = compatible;

  if (favoritePairs.length && discoveryPairs.length) {
    const explore = seededUnit(seed, "secondary-discovery") < 0.35;
    secondaryPool = explore ? discoveryPairs : favoritePairs;
  }

  const secondary = weightedPick(secondaryPool, seed + 431, 3);
  if (!secondary) return result;

  result.push(enforceVolumeContrast(result[0], toAmbiance(secondary, "secondary", preferences, intent)));
  return result;
}

function buildWhy({ ambiances, breathing, preferences, intent }) {
  const reasons = [];
  const primary = ambiances[0];

  if (primary?.favorite) {
    reasons.push(`${primary.name} fait partie de vos ambiances favorites.`);
  } else if (primary?.strength === 3) {
    reasons.push(
      `${primary.name} correspond particulièrement bien à ce dont vous avez besoin maintenant.`,
    );
  }

  const naturalStat = preferences?.learning?.naturals?.[intent]?.[primary?.id];

  if (Number(naturalStat?.samples) >= 3 && Number(naturalStat?.mean) > 0.4) {
    reasons.push(
      "Vos précédents ressentis indiquent que cette ambiance vous accompagne bien.",
    );
  }

  if (breathing?.reason) reasons.push(breathing.reason);
  return reasons.slice(0, 2);
}

function normalizeCompositionDuration(value = "") {
  return VALID_AURIA_DURATIONS.has(value) ? value : "2min";
}

function normalizeStateBefore(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.max(0, Math.min(10, numeric)) : 5;
}

function collectChoiceTrackIds(items = []) {
  const ids = new Set();

  for (const item of Array.isArray(items) ? items : []) {
    for (const track of item?.arc?.tracks || []) {
      if (track?.id !== null && track?.id !== undefined) {
        ids.add(track.id);
      }
    }
  }

  return Array.from(ids);
}

export function composeSession({
  intent,
  stateBefore = 5,
  durationKey,
  uid = "anon",
  isPremium = true,
  naturalSounds = [],
  history = { recentTrackIds: [], recentAmbianceIds: [] },
  preferences = null,
  favoriteNatureSoundIds = [],
  hiddenTrackIds = [],
  favoriteTrackIds = [],
  excludeTrackIds = [],
  excludeBreathingIds = [],
  regenerationCount = 0,
}) {
  if (!isAuriaIntent(intent)) return null;
  if (!VALID_AURIA_DURATIONS.has(durationKey)) return null;

  const seed = rotatingSeed(uid, regenerationCount);
  const learned = preferences || loadPreferencesSync(uid);
  const effectiveFavoriteIds = Array.isArray(learned.favoriteNatureSoundIds) && learned.favoriteNatureSoundIds.length ? learned.favoriteNatureSoundIds : favoriteNatureSoundIds;

  const arc = buildSessionArc({
    intent,
    durationKey,
    recentTrackIds: history.recentTrackIds || [],
    hiddenTrackIds,
    favoriteTrackIds,
    excludeTrackIds,
    seed,
    preferences: learned,
  });

  if (!arc?.tracks?.length) return null;

  const ambiances = pickAmbiances({
    intent,
    durationKey,
    naturalSounds,
    isPremium,
    seed,
    recentAmbianceIds: history.recentAmbianceIds || [],
    favoriteNatureSoundIds: effectiveFavoriteIds,
    preferences: learned,
  });

  const breathing = pickBreathing({
    stateBefore,
    intent,
    preferences: learned,
    excludeBreathingIds,
    regenerationCount,
  });

  if (arc.tracks.some((track) => track.intent !== intent)) return null;
  if (breathing && breathing.intent !== intent) return null;

  const durationSeconds = SESSION_TARGETS[durationKey];
  const session = {
    id: `auria-${Date.now()}-${Math.abs(seed % 10000)}`,
    schemaVersion: 1,
    sessionType: "auria-personalized",
    durationKey,
    arc: {
      ...arc,
      crossfadeSeconds: CROSSFADE_SECONDS,
    },
    ambiances,
    breathing,
    narrative: generateNarrative({ intent, seed }),
    metadata: {
      humanComposed: true,
      durationSeconds,
      dominantHz: arc.tracks[0]?.fundamentalHz || null,
      tuning: arc.tracks[0]?.tuning || null,
    },
    context: {
      intent,
      stateBefore,
      durationKey,
      durationSeconds,
      durationMinutes: durationSeconds / 60,
      regenerationCount,
      generatedAt: new Date().toISOString(),
    },
  };

  session.why = buildWhy({
    ambiances,
    breathing,
    preferences: learned,
    intent,
  });

  return session;
}

/*
 * Point d'entrée partagé par le portail Auria et le Refuge du parcours manuel.
 * La durée de 30 secondes est transmise telle quelle au compositeur : aucun
 * faux passage par une composition de deux minutes n'est nécessaire.
 */
export async function composeAuriaMoment({
  uid,
  intent,
  durationKey,
  stateBefore = 5,
  naturalSounds = [],
  favoriteNatureSoundIds = [],
  currentStreak = 0,
  excludeTrackIds = [],
  excludeBreathingIds = [],
  regenerationCount = 0,
  isPremium = true,
  saveStart = true,
}) {
  if (!uid) throw new Error("Utilisateur absent");
  if (!isAuriaIntent(intent)) throw new Error("Intention Auria invalide");

  const normalizedDuration = normalizeCompositionDuration(durationKey);
  const normalizedState = normalizeStateBefore(stateBefore);

  const preferences = await loadPreferences(uid);
  const hiddenTrackIds = collectChoiceTrackIds(preferences.hiddenSessions || []);
  const favoriteTrackIds = collectChoiceTrackIds(preferences.favorites || []);

  const session = composeSession({
    stateBefore: normalizedState,
    intent,
    durationKey: normalizedDuration,
    uid,
    isPremium,
    naturalSounds,
    history: {
      recentTrackIds: getRecentTrackIds(uid),
      recentAmbianceIds: getRecentAmbianceIds(uid),
    },
    preferences,
    favoriteNatureSoundIds,
    hiddenTrackIds,
    favoriteTrackIds,
    excludeTrackIds,
    excludeBreathingIds,
    regenerationCount,
  });

  if (!session?.arc?.tracks?.length) {
    throw new Error("Aucune composition disponible");
  }

  const withBreathing = Boolean(session.breathing);
  const completedSession = {
    ...session,
    durationKey: normalizedDuration,
    context: {
      ...(session.context || {}),
      intent,
      durationKey: normalizedDuration,
      stateBefore: normalizedState,
      withBreathing,
    },
  };

  if (saveStart) await saveSessionStart(uid, completedSession, normalizedState);

  return {
    session: completedSession,
    stateBefore: normalizedState,
    withBreathing,
  };
}
